import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  SButton,
  SCard,
  SHeading2,
  SIcon,
  SInput,
  SNotification,
} from '@chargebee/sting-react';
import { BlackToastTrigger } from '@bbkAdminComponents/alerts/black-toast';
import { CBRetentionLogo } from '@bbkAdminComponents/cbretention-logo';
import { useInfoModal } from '@bbkAdminComponents/info-dialog';
import type * as LogInTypes from '@bbkAdminComponents/log-in-flow/log-in-types';
import {
  preauthCompanySlice,
  type CheckUsernameResponse,
} from '@bbkAdminRedux/rtkq/preauth-company.slice';
import {
  parseRTKAPIError,
  type RTKAPIErrorResponse,
} from '@bbkAdminRedux/rtkq/rtkq-error-logger';
import { FeatureFlagsE, useFeatureFlags } from '@bbkAdminUtils/feature-flags';
import { GlobalLogger } from '@bbkAdminUtils/GlobalLogger';

type SignInTypes = {
  error?: LogInTypes.Error;
  success?: LogInTypes.Success;
  formValues: LogInTypes.FormValues;
  chargebeeSignIn: LogInTypes.ChargebeeSignIn;
  signIn: LogInTypes.SignIn;
  sending: LogInTypes.Sending;
  isCBPath?: boolean;
};
export const SignIn: React.FC<SignInTypes> = ({
  formValues,
  signIn,
  chargebeeSignIn,
  error,
  sending,
  success,
  isCBPath,
}) => {
  const { email, password, setEmail, setPassword } = formValues;

  const pwdRef = useRef<HTMLInputElement>(null);
  const { triggerModal } = useInfoModal();

  const isDevLoginAuthEnabled = useFeatureFlags(
    FeatureFlagsE.ENABLE_DEV_LOGIN_AUTH
  );
  const { data: clientConfig } =
    preauthCompanySlice.endpoints.getClientConfig.useQuery();
  // Username/password and Google have been removed; dev-identity login is the
  // only remaining password-based flow, and it only works when the backend dev
  // identity provider is on (`/dev/login` rejects otherwise). Gating password
  // availability on it keeps the password field from appearing when a sign-in
  // attempt would be rejected server-side.
  const isDevIdentityLogin =
    isDevLoginAuthEnabled && Boolean(clientConfig?.dev_identity_provider);
  const shouldShowPasswordAuth = isDevIdentityLogin;
  const [showPassword, setShowPassword] = useState(false);

  const [cbidpCheckUsername$] =
    preauthCompanySlice.endpoints.cbidpCheckUsername.useLazyQuery();
  const [cbidpInitiateMigration$] =
    preauthCompanySlice.endpoints.cbidpInitiateMigration.useLazyQuery();

  // Sequence guard: only the most recent username check may apply its result,
  // so overlapping Continue clicks cannot sign in / migrate a stale email.
  const checkSeq = useRef(0);

  useEffect(() => {
    if (shouldShowPasswordAuth) {
      pwdRef.current?.focus();
    }
  }, [shouldShowPasswordAuth]);

  const checkUsername = useCallback(
    async (em: string) => {
      if (!em?.trim()) return;

      const seq = ++checkSeq.current;
      try {
        const response: CheckUsernameResponse = await cbidpCheckUsername$(
          { email: em },
          true
        ).unwrap();
        // Discard a stale lookup if a newer Continue click has since started.
        if (seq !== checkSeq.current) return;

        if (response.valid) {
          chargebeeSignIn(em, response.url);
        } else if (response?.code !== 'not_found') {
          // Only migrate after a successful username check that found the email;
          // a failed lookup must not initiate migration.
          cbidpInitiateMigration$({ email: em })
            .unwrap()
            .then(() => {
              if (seq !== checkSeq.current) return;
              triggerModal({
                title: <>Login system update</>,
                description: (
                  <>
                    We are updating our login system to make it easier for our
                    customers to access all Chargebee products. Please check
                    your email for instructions to set up your credentials.
                  </>
                ),
              });
            })
            .catch((e: RTKAPIErrorResponse) => {
              const err = parseRTKAPIError(e);
              if (seq === checkSeq.current) {
                BlackToastTrigger({
                  content:
                    'Failed to Initiate Account Migration. Please check your email.',
                });
              }
              GlobalLogger.error(
                `cbidpInitiateMigration error: ${err?.message}`
              );
            });
        } else {
          BlackToastTrigger({
            content: 'Please continue with Chargebee to sign in.',
          });
        }
      } catch (e) {
        GlobalLogger.error(e);
        if (seq === checkSeq.current) {
          BlackToastTrigger({
            content: 'Could not check email. Please try again',
          });
        }
      }
    },
    [
      cbidpCheckUsername$,
      chargebeeSignIn,
      cbidpInitiateMigration$,
      triggerModal,
    ]
  );

  return (
    <div
      className="sign-in-page tw-flex tw-min-h-screen tw-w-full tw-flex-col tw-items-center tw-justify-start tw-px-4 tw-py-8 sm:tw-pt-16 lg:tw-pt-24"
      data-testid="sign-in-page"
    >
      <div className="sign-in-card-shell tw-w-full tw-max-w-[420px]">
        <SCard
          padding="large"
          rounded
          className="!tw-border !tw-border-neutral-200"
        >
          <div className="tw-flex tw-flex-col tw-gap-6">
            <div className="tw-flex tw-flex-col tw-items-center tw-gap-3 tw-text-center">
              <CBRetentionLogo className="tw-h-10" />
              <SHeading2
                as="h1"
                className="!tw-text-xl !tw-font-semibold !tw-text-neutral-900 sm:!tw-text-2xl"
              >
                Sign-in to continue
              </SHeading2>
            </div>

            {success && (
              <SNotification
                variant="success"
                hasBorder
                title={success.title}
                description={success.message}
              />
            )}
            {error && (
              <SNotification
                variant="danger"
                hasBorder
                title={error.title}
                description={error.message}
              />
            )}

            <form
              onSubmit={
                shouldShowPasswordAuth ? signIn : (e) => e.preventDefault()
              }
              className="tw-flex tw-flex-col tw-gap-4"
              data-testid="sign-in-form"
            >
              <SInput
                label="Email"
                type="email"
                name="email"
                autoComplete="on"
                placeholder="Enter your email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={() => {
                  setShowPassword(false);
                }}
                disabled={sending || isCBPath}
              />

              {shouldShowPasswordAuth && (
                <SInput
                  ref={pwdRef}
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  autoComplete="on"
                  placeholder="Password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={sending}
                  append={
                    <SButton
                      type="button"
                      variant="neutral-ghost"
                      size="small"
                      className="!tw-h-7 !tw-w-7 !tw-p-0"
                      onClick={() => setShowPassword((current) => !current)}
                      aria-label={showPassword ? 'Hide' : 'Show'}
                      disabled={sending}
                    >
                      <SIcon
                        name={showPassword ? 'eye-off' : 'eye'}
                        size={18}
                      />
                    </SButton>
                  }
                />
              )}

              {shouldShowPasswordAuth ? (
                <SButton
                  type="submit"
                  variant="primary"
                  size="large"
                  fullWidth
                  className="submitButtonFooter"
                  disabled={sending}
                  loading={sending}
                  icon={<SIcon name="arrow-right" size={16} />}
                  iconPosition="right"
                >
                  Sign in
                </SButton>
              ) : (
                <SButton
                  type="button"
                  variant="primary"
                  size="large"
                  fullWidth
                  className="submitButtonFooter"
                  disabled={sending}
                  icon={<SIcon name="arrow-right" size={16} />}
                  iconPosition="right"
                  onClick={() => {
                    void checkUsername(email);
                  }}
                >
                  Continue
                </SButton>
              )}
            </form>
          </div>
        </SCard>
      </div>
    </div>
  );
};
